import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { cfpBaseUrl } from '../../network/urls';

/**
 * @typedef {Object} StatsState
 * @property {number|null} totalNewConnectionApplications - Total number of new connection applications.
 * @property {number|null} totalChangeOfLoadApplications - Total number of change of load applications.
 * @property {number|null} totalChangeOfNameApplications - Total number of change of name applications.
 * @property {number|null} totalChangeOfTariffApplications - Total number of change of tariff applications.
 * @property {number|null} totalChangeOfMeterRequrestApplications - Total number of change of meter request applications.
 * @property {number|null} totalOptForSmartMeterApplications - Total number of opt-for-smart-meter applications.
 * @property {number|null} totalDueDateExtensionApplications - Total number of due date extension applications.
 * @property {number|null} totalBillInstallmentApplications - Total number of bill installment applications.
 * @property {number|null} totalComplaintsApplications - Total number of complaints applications.
 * @property {number|null} totalMeterReadingCorrectionApplications - Total number of meter reading correction applications.
 * @property {number|null} totalNetMeteringApplications - Total number of net metering applications.
 * @property {boolean} loading - Indicates if the API call is in progress.
 * @property {string|null} error - Stores any error message if API call fails.
 */

const initialState = {
    totalNewConnectionApplications: null,
    totalChangeOfLoadApplications: null,
    totalChangeOfNameApplications: null,
    totalChangeOfTariffApplications: null,
    totalChangeOfMeterRequrestApplications: null,
    totalOptForSmartMeterApplications: null,
    totalDueDateExtensionApplications: null,
    totalBillInstallmentApplications: null,
    totalComplaintsApplications: null,
    totalMeterReadingCorrectionApplications: null,
    totalNetMeteringApplications: null,
    loading: false,
    error: null,
}

/**
 * Async thunk to fetch all application statistics.
 *
 * @function allStatsAPI
 * @async
 * @memberof Features
 * @param {Object} data - API request payload.
 * @param {string} data.officeCode - Office code for the API request.
 * @param {string} data.officeLevel - Office level for the API request.
 * @param {boolean} data.showAllApplicationsFlag - Flag to show all applications.
 * @param {Object} thunkAPI - The Redux Toolkit thunk API object.
 * @param {Function} thunkAPI.getState - Function to get the Redux state.
 * @param {Function} thunkAPI.rejectWithValue - Function to return error values.
 * @returns {Promise<Object>} The API response containing application statistics.
 */
export const allStatsAPI = createAsyncThunk(
    'allStats',
    async (data, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const { token } = auth;
        if (token) {
            try {

                const requestOptions = {
                    method: 'POST',
                    headers: {
                        'Authorization': token, // Adjust here if your token doesn't include 'Bearer '
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        "officeCode": data.officeCode,
                        "officeLevel": data.officeLevel,
                        "showAllApplicationFlag": data.showAllApplicationsFlag
                    })
                };

                const response = await fetch(cfpBaseUrl + 'comman/totalCountofAllApplications', requestOptions);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Stats API issue!');
                }

                const result = await response.json();

                if (result.status !== "200") {
                    throw new Error(result.message || 'Unable to fetch stats!');
                }

                return result;
            } catch (error) {
                return rejectWithValue(error.message);
            }
        }
    }
);

/**
 * Redux slice for managing application statistics.
 * This slice handles state related to various application statistics.
 */
const statsSlice = createSlice({
    name: 'stats',
    initialState,
    reducers: {
        /**
         * Resets all statistics values to null and clears loading/error states.
         *
         * @function removeStats
         * @param {StatsState} state - The current state.
         */
        removeStats: (state) => {
            state.totalNewConnectionApplications = null
            state.totalChangeOfLoadApplications = null
            state.totalChangeOfNameApplications = null
            state.totalChangeOfTariffApplications = null
            state.totalChangeOfMeterRequrestApplications = null
            state.totalOptForSmartMeterApplications = null
            state.totalDueDateExtensionApplications = null
            state.totalBillInstallmentApplications = null
            state.totalComplaintsApplications = null
            state.totalMeterReadingCorrectionApplications = null
            state.totalNetMeteringApplications = null
            state.loading = false
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            /**
             * Handles the pending state when fetching statistics.
             * @function allStatsAPI.pending
             * @param {StatsState} state - The current state.
             */
            .addCase(allStatsAPI.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            /**
             * Handles the fulfilled state when statistics are successfully fetched.
             * @function allStatsAPI.fulfilled
             * @param {StatsState} state - The current state.
             * @param {Object} action - The action payload.
             * @param {Object} action.payload - The API response.
             * @param {Object[]} action.payload.data - The statistics data array.
             */
            .addCase(allStatsAPI.fulfilled, (state, action) => {
                if (action.payload) {
                    state.totalNewConnectionApplications = action.payload.data[0].totalNewConnectionApplications
                    state.totalChangeOfLoadApplications = action.payload.data[0].totalChangeOfLoadApplications
                    state.totalChangeOfNameApplications = action.payload.data[0].totalChangeOfNameApplications
                    state.totalChangeOfTariffApplications = action.payload.data[0].totalChangeOfTariffApplications
                    state.totalChangeOfMeterRequrestApplications = action.payload.data[0].totalChangeOfMeterRequrestApplications
                    state.totalOptForSmartMeterApplications = action.payload.data[0].totalOptForSmartMeterApplications
                    state.totalDueDateExtensionApplications = action.payload.data[0].totalDueDateExtensionApplications
                    state.totalBillInstallmentApplications = action.payload.data[0].totalBillInstallmentApplications
                    state.totalComplaintsApplications = action.payload.data[0].totalComplaintsApplications
                    state.totalMeterReadingCorrectionApplications = action.payload.data[0].totalMeterReadingCorrectionApplications
                    state.totalNetMeteringApplications = action.payload.data[0].totalNetMeteringApplications
                    state.loading = false
                    state.error = null
                }
            })
            /**
             * Handles the rejected state when fetching statistics fails.
             * @function allStatsAPI.rejected
             * @param {StatsState} state - The current state.
             * @param {Object} action - The action payload.
             * @param {string} action.payload - The error message.
             */
            .addCase(allStatsAPI.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { removeStats } = statsSlice.actions;
export default statsSlice.reducer;